import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, User, Target } from "lucide-react";
import { formatDateForSouthAfrica } from "@/lib/textUtils";
import { getStatusColor } from "@/lib/jobUtils";
import { getInitials } from "@/lib/jobDetailsUtils";
import { ReferralStatus, RecommendationStatus } from "@/lib/schema";
import { AdminReferralStatusBadge } from "@/components/admin/AdminReferralStatusBadge";

export interface ReferralTableRowData {
  id: string;
  candidateName: string;
  candidateEmail?: string;
  referrerName: string;
  status: ReferralStatus;
  matchingScore: number;
  recommendation?: RecommendationStatus;
  createdAt: Date;
}

interface ReferralTableRowProps {
  referral: ReferralTableRowData;
  onViewDetails?: (referralId: string) => void;
  showActions?: boolean;
  showMatchingScore?: boolean;
  showRecommendation?: boolean;
  className?: string;
}

const getRecommendationColor = (recommendation: RecommendationStatus) => {
  switch (recommendation) {
    case RecommendationStatus.HIGHLY_RECOMMENDED:
      return 'bg-green-100 text-green-800';
    case RecommendationStatus.RECOMMENDED:
      return 'bg-blue-100 text-blue-800';
    case RecommendationStatus.CONSIDER:
      return 'bg-yellow-100 text-yellow-800';
    case RecommendationStatus.NOT_RECOMMENDED:
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export function ReferralTableRow({ 
  referral, 
  onViewDetails,
  showActions = true,
  showMatchingScore = true,
  showRecommendation = true,
  className = ""
}: ReferralTableRowProps) {
  return (
    <tr 
      className={`hover:bg-slate-50 ${className}`} 
      data-testid={`referral-row-${referral.id}`}
    >
      {/* Candidate Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <Avatar className="h-8 w-8 mr-3">
            <AvatarFallback className="bg-slate-300 text-slate-700 text-xs">
              {getInitials(referral.candidateName)}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="text-sm font-medium text-slate-900 space-grotesk">
              {referral.candidateName}
            </div>
            {referral.candidateEmail && (
              <div className="text-xs text-slate-500 space-grotesk">
                {referral.candidateEmail}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Referrer Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-slate-400" />
          <span className="text-sm text-slate-900 space-grotesk">{referral.referrerName}</span>
        </div>
      </td>

      {/* Status Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <AdminReferralStatusBadge 
          status={referral.status}
          size="sm"
        />
      </td>

      {/* Match Score Column (conditional) */}
      {showMatchingScore && (
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <div className="w-12 bg-slate-200 rounded-full h-2">
              <div 
                className="bg-orange-500 h-2 rounded-full" 
                style={{ width: `${referral.matchingScore}%` }}
              ></div>
            </div>
            <span className="text-sm font-medium text-slate-900 space-grotesk">{referral.matchingScore}%</span>
          </div>
        </td>
      )}

      {/* Recommendation Column (conditional) */}
      {showRecommendation && (
        <td className="px-6 py-4 whitespace-nowrap">
          {referral.recommendation ? (
            <div className="flex items-center gap-2">
              <Badge className={`${getRecommendationColor(referral.recommendation)} border-0 text-xs font-medium`}>
                {referral.recommendation.replace('_', ' ').split(' ').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ')}
              </Badge>
            </div>
          ) : (
            <span className="text-xs text-slate-400 space-grotesk">Pending</span>
          )}
        </td>
      )}

      {/* Submitted Date Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">
            {formatDateForSouthAfrica(referral.createdAt)}
          </span>
        </div>
      </td>

      {/* Actions Column (conditional) */}
      {showActions && (
        <td className="px-6 py-4 whitespace-nowrap">
          {onViewDetails && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onViewDetails(referral.id)}
              data-testid={`button-view-details-${referral.id}`}
              className="space-grotesk font-medium"
            >
              View
            </Button>
          )}
        </td>
      )}
    </tr>
  );
}
